import React, { useState } from 'react';
import { Mail, Copy, Zap, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Place, EmailData } from '../../../types/results.types';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useAppSelector } from '../../../hooks/reduxHooks';
import EmailVerificationModal from '../EmailVerificationModal/EmailVerificationModal';
import SingleEmailVerifyButton from '../EmailQualityBadge/SingleEmailVerifyButton';
import HoverTooltip from '../../common/HoverTooltip/HoverTooltip';
import { isVerifiable } from '../../../utils/emailVerifiable';
import EmailBadge, { CatchAllBadge } from '../EmailBadge/EmailBadge';
import { getEmailProvenance } from '../../../utils/resultProvenance';

interface EmailsTabProps {
  place: Place;
  searchId?: number;
}

const EmailsTab: React.FC<EmailsTabProps> = ({ place, searchId }) => {
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [verifyAllOpen, setVerifyAllOpen] = useState(false);
  const { hasVerificationAccess, hasConfidenceBadgesAccess } = useFeatureAccess();
  const { loading: verificationLoading, activeVerification } = useAppSelector(s => s.emailVerification);
  const isVerifying = verificationLoading || !!(activeVerification && activeVerification.status === 'processing');

  const companyEmails = place.company_emails || [];
  const catchAllEmail = companyEmails.find(e => e.is_catch_all === true);
  const catchAllDomain = catchAllEmail ? catchAllEmail.email.split('@')[1] : null;
  const verifiableEmails = companyEmails.filter(isVerifiable);

  const handleCopy = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 1800);
    } catch {}
  };

  if (companyEmails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Mail className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm font-semibold text-gray-500">No additional company emails found</p>
        <p className="text-xs text-gray-400 mt-1">No extra business addresses were collected for this company.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Catch-all domain banner */}
      {catchAllDomain && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
              Catch-all domain
            </span>
            <span className="text-xs font-mono text-amber-800">@{catchAllDomain}</span>
          </div>
          <p className="text-xs text-amber-800 leading-relaxed ml-5">
          Emails on {catchAllDomain} can't be verified reliably because this domain accepts any address.{' '}
            <HoverTooltip content="Catch-all domains accept mail to any address, even ones that don't exist. Verification confirms the domain is live but can't verify whether this specific inbox exists.">
              <span className="underline cursor-help">Why does this happen?</span>
            </HoverTooltip>
          </p>
          {/* <p className="text-xs text-amber-700 font-medium mt-1 ml-5">
            Export as-is — verification can't confirm individual inboxes on this domain.
          </p> */}
        </div>
      )}

      {/* Recommended next step banner */}
      {verifiableEmails.length > 0 && hasVerificationAccess && searchId && (
        <div className="flex items-center gap-4 bg-[#2D3142] rounded-xl px-4 py-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Recommended next step</p>
            <p className="text-sm font-bold text-white leading-snug">
              Verify discovered {verifiableEmails.length === 1 ? 'address' : 'addresses'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Refunded automatically if invalid</p>
          </div>
          <button
            onClick={() => setVerifyAllOpen(true)}
            disabled={isVerifying}
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 bg-white text-[#2D3142] text-xs font-bold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Verify all →
          </button>
        </div>
      )}

      {/* Section header */}
      <div className="pt-1">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Additional company emails ({companyEmails.length})
        </span>
        <p className="mt-0.5 text-xs text-gray-400">
          Emails found for this company that are not linked to an identified person.
        </p>
      </div>

      {/* Email cards */}
      <div className="space-y-2">
        {companyEmails.map((email: EmailData) => {
          const provenance = getEmailProvenance(email);

          return (
            <div
              key={email.id ?? email.email}
              className="flex items-start gap-3 p-3 rounded-xl border border-[#F9F5F5] bg-white hover:shadow-sm transition-shadow duration-150"
            >
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-purple-600" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono font-semibold text-black truncate">{email.email}</p>
                <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[10px] text-gray-400">
                  <span className="flex-shrink-0">Possible staff email</span>
                  {provenance && (
                    <>
                      <span>·</span>
                      <span className="truncate text-[#8A3033]">{provenance.label}</span>
                    </>
                  )}
                </div>
                {provenance?.sourceUrl && provenance.sourceLabel && (
                  <a
                    href={provenance.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-[10px] text-[#8A3033] underline underline-offset-2 hover:text-[#722629] truncate"
                  >
                    {provenance.sourceLabel}
                  </a>
                )}
              </div>

              {email.is_catch_all && !catchAllDomain
                ? <CatchAllBadge />
                : !email.is_catch_all && hasConfidenceBadgesAccess && <EmailBadge email={email} />}

              <button
                onClick={() => handleCopy(email.email)}
                className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-[#8A3033] transition-colors"
                title="Copy email"
              >
                {copiedEmail === email.email
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  : <Copy className="w-3.5 h-3.5" />
                }
              </button>

              {hasVerificationAccess && searchId && isVerifiable(email) && (
                <SingleEmailVerifyButton email={email} searchId={searchId} />
              )}
            </div>
          );
        })}
      </div>

      {/* Verify all modal */}
      {verifyAllOpen && searchId && (
        <EmailVerificationModal
          isOpen={verifyAllOpen}
          onClose={() => setVerifyAllOpen(false)}
          personEmails={[]}
          placeEmails={verifiableEmails}
          emailType="place"
          searchId={searchId}
          bulkMode={true}
        />
      )}
    </div>
  );
};

export default EmailsTab;
